import { api, type ApiSchema } from "@/modules/api";
import { LensKind, SortByKind } from "@/modules/lenses/types";
import type { AppStore } from "@/store/AppStore";
import type { SearchNotesInput, SearchNotesOutput } from "@mem-client/ai-toolkit/contract";
import {
  AiToolkitToolName,
  searchNotesInputSchema,
  searchNotesOutputSchema,
} from "@mem-client/ai-toolkit/contract";
import type { AiToolkitToolDefinition } from "@/modules/ai-toolkit/tools/types";
import {
  buildLocalNote,
  DEFAULT_PREVIEW_CHAR_LIMIT,
  getNoteIdFromIndexTuple,
  isLocalNote,
} from "@/modules/ai-toolkit/tools/notes/note-tools.helpers";

const DEFAULT_SEARCH_LIMIT = 10;

type SearchEngineResult = ApiSchema["SearchEngineResult"];
type SearchEngineItemResult = ApiSchema["SearchEngineResultSearchEngineResultSectionItem"];
type SearchEngineItemValue = SearchEngineItemResult["value"];
type SearchEngineNoteItemValue =
  ApiSchema["SearchEngineResultSectionItemValueSearchEngineResultSectionItemNote"];

export const searchNotes = async ({
  store,
  input,
}: {
  store: AppStore;
  input: SearchNotesInput;
}): Promise<SearchNotesOutput> => {
  const normalizedQuery = input.query?.trim();
  const limit = input.limit ?? DEFAULT_SEARCH_LIMIT;

  const noteIds = normalizedQuery
    ? await searchNoteIdsViaSmartSearch({ store, query: normalizedQuery, limit })
    : await listRecentNoteIdsLocally({ store, limit });

  const localNotes = await Promise.all(
    noteIds.map(async noteId => {
      const note = await store.notes.getAsync(noteId);

      if (!note?.isAvailable) {
        return null;
      }

      return buildLocalNote({
        store,
        note,
        includeContent: input.includeContentPreview ? "plaintext" : "none",
        maxContentChars: input.maxContentChars ?? DEFAULT_PREVIEW_CHAR_LIMIT,
        missingContentBehavior: "return_status",
      });
    })
  );

  return { notes: localNotes.filter(isLocalNote) };
};

const isSearchEngineItemResult = (result: SearchEngineResult): result is SearchEngineItemResult =>
  result.kind === "ITEM";

const isSearchEngineNoteItemValue = (
  value: SearchEngineItemValue
): value is SearchEngineNoteItemValue => value.kind === "NOTE" && "note_id" in value.value;

const getNoteIdFromSmartSearchResult = (result: SearchEngineResult): string | null => {
  if (!isSearchEngineItemResult(result) || !isSearchEngineNoteItemValue(result.value)) {
    return null;
  }

  return result.value.value.note_id;
};

const searchNoteIdsViaSmartSearch = async ({
  store,
  query,
  limit,
}: {
  store: AppStore;
  query: string;
  limit: number;
}): Promise<string[]> => {
  const spaceId = store.spaces.myPersonalSpaceId;
  const response = await api.post("/v2/smart_search", {
    params: { query: { space_id: spaceId } },
    body: {
      space_id: spaceId,
      pagination_info: { sort_by: "RELEVANCE", page_size: limit },
      query,
      filters: [],
      facet_filters: [],
      config: { supported_section_kinds: ["ITEM"] },
    },
  });

  if (response.error) {
    throw new Error(`smart_search failed: ${JSON.stringify(response.error)}`);
  }

  const noteIds: string[] = [];
  for (const result of response.data?.results ?? []) {
    const noteId = getNoteIdFromSmartSearchResult(result);

    if (!noteId) {
      continue;
    }

    noteIds.push(noteId);
    if (noteIds.length >= limit) {
      break;
    }
  }

  return noteIds;
};

const listRecentNoteIdsLocally = async ({
  store,
  limit,
}: {
  store: AppStore;
  limit: number;
}): Promise<string[]> => {
  const noteIndexTuples = await store.notes.search({
    lens: LensKind.All,
    sortBy: SortByKind.LastModified,
    limit,
  });
  return noteIndexTuples.map(getNoteIdFromIndexTuple);
};

export const searchNotesTool: AiToolkitToolDefinition<
  AiToolkitToolName.SearchNotes,
  SearchNotesInput,
  SearchNotesOutput
> = {
  name: AiToolkitToolName.SearchNotes,
  inputSchema: searchNotesInputSchema,
  outputSchema: searchNotesOutputSchema,
  strict: true,
  execute: searchNotes,
};
